// ERRORS-1: client-side diagnostics for bug reports (docs/specs/errors-1.md §7).
//
// A small in-memory ring buffer of recent error CODES (never messages — a server message or a
// thrown error's text could echo user content) plus the request ids the server stamped on
// them, and a collector that assembles the allowlisted `BugReportDiagnostics` shape. Nothing
// here reads transcript text, titles, emails or audio, and nothing is persisted: a reload
// clears it.
import { ScribeApiError } from '@scribe/core/client';
import { problemCodeForApiError } from '@scribe/core/errors';
import type { BugReportDiagnostics, ClientErrorEvent } from '@scribe/core/types';

const MAX_EVENTS = 20;
const events: ClientErrorEvent[] = [];

export function recordClientError(event: {
  code: string;
  status?: number | null;
  requestId?: string | null;
}): void {
  const code = event.code.toLowerCase().replace(/[^a-z0-9_.]/g, '_').slice(0, 64);
  if (!code) return;
  events.push({
    code,
    at: new Date().toISOString(),
    ...(event.requestId && /^[A-Za-z0-9-]{1,64}$/.test(event.requestId)
      ? { request_id: event.requestId }
      : {}),
    ...(event.status != null ? { status: event.status } : {}),
  });
  if (events.length > MAX_EVENTS) events.splice(0, events.length - MAX_EVENTS);
}

/** Record any thrown error — used by the React Query caches' `onError`. */
export function recordThrownError(err: unknown): void {
  if (err instanceof ScribeApiError) {
    recordClientError({
      code: err.code ?? problemCodeForApiError(err),
      status: err.status,
      requestId: err.requestId ?? null,
    });
    return;
  }
  recordClientError({ code: problemCodeForApiError(err, navigator.onLine) });
}

export function recentClientErrors(): ClientErrorEvent[] {
  return events.slice();
}

/** Test seam. */
export function clearClientErrors(): void {
  events.length = 0;
}

/** Coarse, non-identifying browser + OS labels ("Chrome 120", "macOS"). */
export function describeUserAgent(ua: string): { browser: string; os: string } {
  const match =
    /Edg\/(\d+)/.exec(ua) ?? /Firefox\/(\d+)/.exec(ua) ?? /Chrome\/(\d+)/.exec(ua) ?? /Version\/(\d+).*Safari/.exec(ua);
  const name = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Other';
  const browser = match ? `${name} ${match[1]}` : name;
  const os = /Mac OS X|Macintosh/.test(ua)
    ? 'macOS'
    : /Windows/.test(ua)
      ? 'Windows'
      : /Android/.test(ua)
        ? 'Android'
        : /iPhone|iPad/.test(ua)
          ? 'iOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : 'Other';
  return { browser, os };
}

/** The allowlisted diagnostics attached to a report. `route` is the path only. */
export function collectDiagnostics(): BugReportDiagnostics {
  const { browser, os } = describeUserAgent(navigator.userAgent);
  const errors = recentClientErrors();
  const requestIds = [...new Set(errors.map((e) => e.request_id).filter((r): r is string => !!r))];
  return {
    app_version: import.meta.env.VITE_APP_VERSION ?? '0.1.0',
    ...(import.meta.env.VITE_APP_BUILD ? { build: import.meta.env.VITE_APP_BUILD } : {}),
    browser,
    os,
    route: window.location.pathname,
    client_errors: errors,
    request_ids: requestIds,
  };
}
